// Section 24.6: zod schemas for registry entries. The schema is the single description of a
// valid entry and doubles as documentation. Hooks are checked to be functions.
import { z } from 'zod';
import { TARGET_KINDS } from './modifiers';

const hook = z.custom<(...args: never[]) => unknown>((v) => typeof v === 'function', {
  message: 'hook must be a function',
});
const kebabId = z.string().regex(/^[a-z][a-z0-9-]*$/, 'ids are kebab-case');
const camelId = z.string().regex(/^[a-z][a-zA-Z0-9]*$/, 'policy ids are camelCase state keys');
const label = z.string().min(1);
const section = z.string().regex(/^S\d+(\.\d+)?$/, "section must look like 'S8' or 'S24.6'");
const amount = z.number().nonnegative();
const probability = z.number().min(0).max(1);
const targets = z.array(z.enum(TARGET_KINDS));
const resource = z.enum(['food', 'water', 'wood', 'stone', 'tools', 'medicine', 'cloth', 'fibre']);
const resourceAmounts = z.partialRecord(resource, amount);
const season = z.enum(['spring', 'summer', 'autumn', 'winter']);

const modifierKeys = z.partialRecord(z.enum(TARGET_KINDS), z.array(z.string().min(1)));
const modifies = {
  modifierTargets: targets,
  modifierKeys: modifierKeys.optional(),
  modifiers: hook,
};

export const jobSchema = z.strictObject({
  id: kebabId,
  label,
  section,
  heavy: z.boolean(),
  output: z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('stock'), resource }),
    z.strictObject({
      kind: z.literal('service'),
      service: z.enum([
        'buildPoints',
        'healingSlots',
        'teachingSeats',
        'careSlots',
        'safetyPoints',
      ]),
    }),
    z.strictObject({ kind: z.literal('none') }),
  ]),
  baseYield: amount,
  toolFactor: z.number().positive().nullable(),
  toolWear: amount,
  risks: z.partialRecord(z.enum(['injury', 'death', 'illness', 'animal']), probability),
  riskIllness: kebabId.nullable(),
  requiresBuilding: kebabId.nullable(),
  minStrength: amount,
  ...modifies,
  produce: hook,
  eligible: hook,
});

export const buildingSchema = z.strictObject({
  id: kebabId,
  label,
  section,
  buildPoints: z.number().positive(),
  materials: resourceAmounts,
  material: z.enum(['wood', 'stone']),
  beds: z.number().int().nonnegative(),
  plots: z.number().int().nonnegative(),
  capacity: resourceAmounts,
  limit: z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('none') }),
    z.strictObject({ kind: z.literal('fixed'), max: z.number().int().positive() }),
    z.strictObject({ kind: z.literal('per-people'), people: z.number().int().positive() }),
  ]),
  ...modifies,
  onComplete: hook,
  onDamaged: hook,
});

export const eventSchema = z.strictObject({
  id: kebabId,
  label,
  section,
  baseWeight: probability,
  seasons: z.array(season).min(1),
  foreshadow: z.strictObject({ chance: probability }).nullable(),
  ledgerTag: z.string().nullable(),
  weight: hook,
  text: hook,
  apply: hook,
  choices: z.array(z.strictObject({ id: kebabId, label, preview: hook.nullable(), apply: hook })),
  modifierTargets: targets,
  modifiers: hook,
});

export const policySchema = z.strictObject({
  id: camelId,
  label,
  section,
  group: z.enum(['rations', 'health', 'work', 'housing', 'education', 'society', 'justice']),
  options: z.array(z.unknown()).min(2).nullable(),
  default: z.unknown(),
  ...modifies,
  changeCost: hook,
  affects: hook,
  validate: hook,
});

export const traitSchema = z.strictObject({
  id: kebabId,
  label,
  section,
  ...modifies,
  restricts: hook,
});

export const illnessSchema = z.strictObject({
  id: kebabId,
  label,
  section,
  source: z.enum(['roll', 'job', 'hunger', 'event']),
  weight: probability,
  untreatedSeasons: z.number().int().positive(),
  treatedSeasons: z.number().int().positive(),
  deathChance: probability,
  contagious: z.boolean(),
  ...modifies,
  onCatch: hook,
  onRecover: hook,
});

export const normSchema = z.strictObject({
  id: kebabId,
  label,
  section,
  establishAfter: z.number().int().positive(),
  decayAfter: z.number().int().positive(),
  reinforcedBy: hook,
  contradictedBy: hook,
  ...modifies,
});

export const priorityGroupSchema = z.strictObject({ id: kebabId, label, section, members: hook });

export const rationPolicySchema = z.strictObject({ id: kebabId, label, section, allocate: hook });

export const contributorSchema = z.strictObject({
  slot: z.enum([
    'season',
    'land',
    'needs',
    'age',
    'illnesses',
    'traits',
    'skills',
    'tools',
    'jobs',
    'buildings',
    'policies',
    'norms',
    'events',
  ]),
  id: z
    .string()
    .regex(/^[a-z]+:[a-zA-Z0-9-]+$/, "contributor ids are namespaced, e.g. 'season:core'"),
  targets,
  only: modifierKeys.optional(),
  modifiers: hook,
});
